import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import "../i18n"; // Import i18n setup

const imagesR1 = [
    { src: "/images/dev_tools/row1/dotNET.svg", text: "dotNET" },
    { src: "/images/dev_tools/row1/MaterialUI.svg", text: "MaterialUI" },
    { src: "/images/dev_tools/row1/Next.svg", text: "NextJS" },
    { src: "/images/dev_tools/row1/PHP.svg", text: "PHP" },
    { src: "/images/dev_tools/row1/React.svg", text: "React" },
    { src: "/images/dev_tools/row1/Spring.svg", text: "Spring" },
    { src: "/images/dev_tools/row1/TailwindCSS.svg", text: "TailwindCSS" },
    { src: "/images/dev_tools/row1/TypeScript.svg", text: "TypeScript" },
    { src: "/images/dev_tools/row1/Vue.svg", text: "Vue" },
    { src: "/images/dev_tools/row1/Swift.svg", text: "Swift" },
    { src: "/images/dev_tools/row1/TensorFlow.svg", text: "TensorFlow" },
];

const imagesR2 = [
    { src: "/images/dev_tools/row2/AWS.svg", text: "AWS" },
    { src: "/images/dev_tools/row2/Cloudflare.svg", text: "Cloudflare" },
    { src: "/images/dev_tools/row2/Flutter.svg", text: "Flutter" },
    { src: "/images/dev_tools/row2/GoogleCloud.svg", text: "GoogleCloud" },
    { src: "/images/dev_tools/row2/Java.svg", text: "Java" },
    { src: "/images/dev_tools/row2/Keras.svg", text: "Keras" },
    { src: "/images/dev_tools/row2/Kotlin.svg", text: "Kotlin" },
    { src: "/images/dev_tools/row2/MicrosoftAzure.svg", text: "MicrosoftAzure" },
    { src: "/images/dev_tools/row2/OpenCV.svg", text: "OpenCV" },
    { src: "/images/dev_tools/row2/Python.svg", text: "Python" },
    { src: "/images/dev_tools/row2/PyTorch.svg", text: "PyTorch" },
];

const TechStack = () => {
    const { t } = useTranslation(['common']);
    const sectionRef = useRef(null);
    const row1Ref = useRef(null);
    const row2Ref = useRef(null);

    useEffect(() => {
        const section = sectionRef.current;
        const row1 = row1Ref.current;
        const row2 = row2Ref.current;

        const handleSectionFocus = () => {
            if (sectionRef.current) {
                const section = sectionRef.current;
                const sectionRect = section.getBoundingClientRect();
                const offset = window.scrollY + sectionRect.top - (window.innerHeight / 2) + (sectionRect.height / 2);

                window.scrollTo({
                    top: offset,
                    behavior: "smooth"
                });
            }
        };

        section?.addEventListener("mouseenter", handleSectionFocus);


        const centerRows = () => {
            if (row1 && row2) {
                row1.scrollTo({
                    left: (row1.scrollWidth - row1.clientWidth) / 2,
                    behavior: "smooth"
                });
                row2.scrollTo({
                    left: (row2.scrollWidth - row2.clientWidth) / 2,
                    behavior: "smooth"
                });
            }
        };

        // Scroll bottom row fully to the right on mount
        const setInitialScroll = () => {
            if (row2) {
                row2.scrollLeft = row2.scrollWidth - row2.clientWidth;
            }
        };

        setInitialScroll();

        const handleMouseEnter = () => centerRows();

        const handleWheel = (e: WheelEvent) => {
            const delta = e.deltaY;

            const row1CanScroll =
                (delta > 0 && row1.scrollLeft < row1.scrollWidth - row1.clientWidth) ||
                (delta < 0 && row1.scrollLeft > 0);

            const row2CanScroll =
                (delta > 0 && row2.scrollLeft > 0) ||
                (delta < 0 && row2.scrollLeft < row2.scrollWidth - row2.clientWidth);

            const shouldIntercept = row1CanScroll || row2CanScroll;

            if (shouldIntercept) {
                e.preventDefault();
                row1.scrollLeft += delta > 0 ? 30 : -30;
                row2.scrollLeft += delta > 0 ? -30 : 30;
            }
        };

        section?.addEventListener("mouseenter", handleMouseEnter);
        section?.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            section?.removeEventListener("wheel", handleWheel);
            section?.removeEventListener("mouseenter", handleMouseEnter); // existing scroll centering
            section?.removeEventListener("mouseenter", handleSectionFocus); // vertical centeringß
        };
    }, []);


    return (
        <section
            id="techstack"
            ref={sectionRef}
            className="relative overflow-y-auto py-4 sm:py-4 md:py-16 lg:py-16 px-2"
        >
            {/* <AnimatedSection> */}
            <div className="container mx-auto flex flex-col lg:flex-row items-center px-6 lg:px-20">
                <div className="w-full p-4">
                    <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
                        {t("common:devTools")}
                    </h1>
                    {/* Fade edges */}
                    {/* <div className="pointer-events-none absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-[#0d1b2a] to-transparent z-10" /> */}
                    {/* <div className="pointer-events-none absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-[#1b263b] to-transparent z-10" /> */}

                    <div className="text-white">
                        {/* Top Row */}
                        <div
                            ref={row1Ref}
                            className="flex py-4 space-x-6 overflow-x-scroll scrollbar-hide transition-all duration-300 mt-6"
                        >
                            {imagesR1.map(({ src, text }, i) => (
                                <motion.div
                                    key={i}
                                    whileHover={{ scale: 1.05, rotateY: 6 }}
                                    transition={{ duration: 0.4 }}
                                    className="relative group w-[80px] sm:w-[100px] md:w-[120px] lg:w-[140px]
               h-22 sm:h-22 md:h-34 lg:h-38
               flex-shrink-0 rounded-xl overflow-hidden shimmer-border"
                                >
                                    {/* Image with blur on hover */}
                                    <img
                                        src={src}
                                        alt={text}
                                        className="w-full h-full object-cover transition-all duration-300 group-hover:blur-sm"
                                    />

                                    {/* Centered text visible only on hover */}
                                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-center">
                                            {text}
                                        </h3>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Bottom Row */}
                        <div
                            ref={row2Ref}
                            className="flex py-4 space-x-6 overflow-x-scroll scrollbar-hide transition-all duration-300"
                        >
                            {imagesR2.map(({ src, text }, i) => (
                                <motion.div
                                    key={i}
                                    whileHover={{ scale: 1.05, rotateY: 6 }}
                                    transition={{ duration: 0.4 }}
                                    className="relative group w-[80px] sm:w-[100px] md:w-[120px] lg:w-[140px]
               h-22 sm:h-22 md:h-34 lg:h-38
               flex-shrink-0 rounded-xl overflow-hidden shimmer-border"
                                >
                                    {/* Image with blur on hover */}
                                    <img
                                        src={src}
                                        alt={text}
                                        className="w-full h-full object-cover transition-all duration-300 group-hover:blur-sm"
                                    />

                                    {/* Centered text visible only on hover */}
                                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-center">
                                            {text}
                                        </h3>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TechStack;
